// Course catalog. 8 canonical offerings split into two tiers.
// IMPORTANT: No course fees are ever displayed on the website. Pricing is confidential.
// Use the "Request Fee Details" CTA which opens the enquiry/lead form.

export const courses = [
  // ============== TIER 1: SHORT COURSES (1-4) ==============
  {
    id: 'single-software-editing',
    slug: 'single-software-editing',
    tier: 'short',
    order: 1,
    title: 'Single Software for Editing',
    tagline: 'Master one industry-standard editor. Get placed.',
    duration: '2.5 to 3.5 months',
    durationDetail: '3.5 months (Avid) / 2.5 months (Final Cut Pro X) / 2.5 months (Adobe Premiere Pro)',
    placement: true,
    placementBadge: '100% Placement Assistance Guarantee',
    description:
      'Pick one industry-standard editor and master it end-to-end. Built for students who want to specialise fast and start working in production.',
    overview:
      'A focused program where you pick one non-linear editor (Avid Media Composer, Final Cut Pro X, or Adobe Premiere Pro) and learn it to a job-ready standard. Work on real footage, build a portfolio piece, complete with placement support.',
    modules: [
      {
        name: 'Avid Media Composer',
        description:
          'Industry-standard non-linear editor used across films and television for large-scale editing workflows.',
      },
      {
        name: 'Final Cut Pro X',
        description:
          'Mac-based editor known for its magnetic timeline and fast workflow, used by filmmakers and content creators.',
      },
      {
        name: 'Adobe Premiere Pro',
        description:
          'Industry-standard editor used across films, TV, and digital content, with integration into the Adobe Creative Cloud ecosystem.',
      },
    ],
    moduleChoice: true,
    languages: ['English', 'Hindi', 'Kannada', 'Tamil', 'Telugu'],
    seo: {
      title: 'Single Software Editing Course in Bangalore: Avid, Final Cut Pro X, Premiere Pro | MatchFrame',
      description:
        'Master one industry-standard editor (Avid, Final Cut Pro X, or Adobe Premiere Pro) at MatchFrame Bangalore. 100% Placement Assistance Guarantee.',
      keywords:
        'Avid course Bangalore, Final Cut Pro X training, Adobe Premiere Pro course Bangalore, single software editing course, video editing institute Bangalore',
    },
  },
  {
    id: 'ai-video-creation',
    slug: 'master-ai-video-creation',
    tier: 'short',
    order: 2,
    title: 'Master in AI Video Creation Course',
    tagline: 'AI-powered video creation in 1 month.',
    duration: '1 month',
    durationDetail: '1 month',
    placement: false,
    addOn: true,
    description:
      'Skill upgrade for editors, marketers, and content creators who want to layer AI video tools into their existing workflow.',
    overview:
      'A one-month skill add-on. Learn modern AI video generation tools, prompt design, and how to integrate AI output into a professional editing pipeline. Not a career-launch program.',
    modules: [
      {
        name: 'AI Video Creation',
        description:
          'AI-powered tools that generate videos from text, images, or templates for fast content creation.',
      },
    ],
    languages: ['English', 'Hindi', 'Kannada', 'Tamil', 'Telugu'],
    seo: {
      title: 'Master in AI Video Creation Course Bangalore: 1 Month Skill Upgrade | MatchFrame',
      description:
        '1-month AI video creation course at MatchFrame Bangalore. Learn AI-powered video generation tools and integrate them into your editing workflow.',
      keywords:
        'AI video creation course Bangalore, AI video editing training, AI video tools course, generative video course',
    },
  },
  {
    id: 'davinci-color-grading',
    slug: 'master-digital-color-grading',
    tier: 'short',
    order: 3,
    title: 'Master in Digital Color Grading Course',
    tagline: 'Become a colorist on DaVinci Resolve.',
    duration: '3.5 months',
    durationDetail: '3.5 months',
    placement: true,
    placementBadge: '100% Placement Assistance Guarantee',
    description:
      'Specialised colorist training on DaVinci Resolve covering grading, conform, finishing, and the full Digital Intermediate workflow.',
    overview:
      'A specialist track for students who want to enter the post-production pipeline as colorists. Covers color theory, primary and secondary grading, node trees, conform, finishing, and delivery for film, OTT, and ads.',
    modules: [
      {
        name: 'DaVinci Resolve',
        description:
          'Professional post-production software combining editing, color grading, visual effects, and audio finishing in one platform.',
      },
    ],
    languages: ['English', 'Hindi', 'Kannada', 'Tamil', 'Telugu'],
    seo: {
      title: 'Master in Digital Color Grading Course Bangalore: DaVinci Resolve | MatchFrame',
      description:
        'Become a professional colorist on DaVinci Resolve at MatchFrame Bangalore. 3.5-month digital color grading course. 100% Placement Assistance Guarantee.',
      keywords:
        'DaVinci Resolve course Bangalore, color grading course, colorist training Bangalore, digital intermediate course, DI training',
    },
  },
  {
    id: 'ai-integrated-editing',
    slug: 'ai-integrated-video-editing',
    tier: 'short',
    order: 4,
    title: 'AI Integrated Video Editing Course',
    tagline: 'Modern editing stack with AI baked in.',
    duration: '4 months',
    durationDetail: '4 months',
    placement: true,
    placementBadge: '100% Placement Assistance Guarantee',
    description:
      'A modern editing program built for the current production reality. Premiere Pro at the core, AI tools on the side, and the motion graphics and design fundamentals studios expect.',
    overview:
      'A 4-month program designed around how editing actually gets done today. Premiere Pro for editorial, AI tools for speed, and After Effects and Photoshop fundamentals so you can finish your own work without waiting on a specialist.',
    modules: [
      { name: 'Adobe Premiere Pro', description: 'Industry-standard video editor for films, TV, and digital content.' },
      { name: 'AI Video Creation', description: 'AI-powered tools that generate videos from text, images, or templates.' },
      { name: 'Basics of After Effects', description: 'Motion graphics and VFX software for cinematic titles, animations, and visual effects.' },
      { name: 'Basics of Photoshop', description: 'Image editing and graphic design software for retouching, compositing, and digital artwork.' },
    ],
    languages: ['English', 'Hindi', 'Kannada', 'Tamil', 'Telugu'],
    seo: {
      title: 'AI Integrated Video Editing Course Bangalore: 4 Months | MatchFrame',
      description:
        '4-month AI integrated video editing course at MatchFrame Bangalore. Premiere Pro, AI tools, After Effects, Photoshop. 100% Placement Assistance Guarantee.',
      keywords:
        'AI video editing course Bangalore, Premiere Pro AI course, video editing with AI tools, integrated editing course',
    },
  },

  // ============== TIER 2: DIPLOMA PROGRAMS (5-8) ==============
  {
    id: 'master-film-editing',
    slug: 'master-in-film-editing',
    tier: 'diploma',
    order: 5,
    title: 'Master in Film Editing Course',
    tagline: 'Cinema-grade editing on Avid and Premiere Pro.',
    duration: '4 months',
    durationDetail: '4 months',
    placement: true,
    placementBadge: '100% Placement Assistance Guarantee',
    description:
      'Cinema-focused editing program covering the two editors that dominate the film and TV pipeline: Avid Media Composer and Adobe Premiere Pro.',
    overview:
      'A 4-month diploma-level track for students serious about a career in film and television editing. Work on long-form footage, learn studio-grade workflows, and finish with a portfolio piece reviewed by working professionals.',
    modules: [
      { name: 'Avid Media Composer', description: 'Professional non-linear editor used in film and television production.' },
      { name: 'Adobe Premiere Pro', description: 'Industry-standard editor with Adobe Creative Cloud integration.' },
      { name: 'Basics of After Effects', description: 'Motion graphics and VFX fundamentals.' },
      { name: 'Basics of Photoshop', description: 'Image editing and design fundamentals.' },
    ],
    languages: ['English', 'Hindi', 'Kannada', 'Tamil', 'Telugu'],
    seo: {
      title: 'Master in Film Editing Course Bangalore: Avid and Premiere Pro | MatchFrame',
      description:
        '4-month Master in Film Editing course at MatchFrame Bangalore. Avid Media Composer, Adobe Premiere Pro, After Effects, Photoshop. 100% Placement Assistance Guarantee.',
      keywords:
        'film editing course Bangalore, Avid course, Master in Film Editing, film editor training Bangalore',
    },
  },
  {
    id: 'master-social-media-editing',
    slug: 'master-in-social-media-editing',
    tier: 'diploma',
    order: 6,
    title: 'Master in Social Media Editing Course',
    tagline: 'Built for the content economy.',
    duration: '8 months',
    durationDetail: '8 months',
    placement: true,
    placementBadge: '100% Placement Assistance Guarantee',
    description:
      'An end-to-end program for social-first editors and content creators. Editorial, color, motion, AI, paid distribution, and design under one roof.',
    overview:
      'Built for the way content gets made and distributed today. Fast-turnaround editing on Premiere Pro and DaVinci Resolve, AI-assisted production, motion graphics, design, and Meta Ads so you understand how content reaches an audience.',
    modules: [
      { name: 'Adobe Premiere Pro', description: 'Industry-standard video editor.' },
      { name: 'DaVinci Resolve', description: 'Editing, color grading, VFX, and audio finishing in one platform.' },
      { name: 'AI Video Creation', description: 'AI-powered fast content generation.' },
      { name: 'Meta Ads', description: 'Digital advertising platform for targeted marketing campaigns on Facebook and Instagram.' },
      { name: 'Basics of After Effects', description: 'Motion graphics and VFX fundamentals.' },
      { name: 'Basics of Photoshop', description: 'Image editing and design fundamentals.' },
    ],
    languages: ['English', 'Hindi', 'Kannada', 'Tamil', 'Telugu'],
    seo: {
      title: 'Master in Social Media Editing Course Bangalore: 8 Months | MatchFrame',
      description:
        '8-month Master in Social Media Editing at MatchFrame Bangalore. Premiere Pro, DaVinci Resolve, AI tools, Meta Ads, After Effects, Photoshop. 100% Placement Assistance Guarantee.',
      keywords:
        'social media editing course Bangalore, content creator course, short-form video editing, Meta Ads course Bangalore',
    },
  },
  {
    id: 'advanced-diploma',
    slug: 'advanced-diploma-film-video-editing',
    tier: 'diploma',
    order: 7,
    title: 'Advanced Diploma in Film & Video Editing',
    tagline: 'The full editor stack in 11 months.',
    duration: '11 months',
    durationDetail: '11 months',
    placement: true,
    placementBadge: '100% Placement Assistance Guarantee',
    featured: true,
    description:
      'The complete editor stack. Every editor that matters in the industry, plus the AI, motion, design, and distribution skills that round out a working professional.',
    overview:
      'An 11-month deep dive into every editing discipline studios hire for. Built for students who want range across film, TV, OTT, social, ads, and the full software arsenal to back it up.',
    modules: [
      { name: 'Avid Media Composer', description: 'Professional non-linear editor for film and TV.' },
      { name: 'Final Cut Pro X', description: 'Mac-based editor with magnetic timeline workflow.' },
      { name: 'Adobe Premiere Pro', description: 'Industry-standard editor with Creative Cloud integration.' },
      { name: 'DaVinci Resolve', description: 'Editing, color grading, VFX, and audio finishing.' },
      { name: 'AI Video Creation', description: 'AI-powered video generation tools.' },
      { name: 'Meta Ads', description: 'Targeted advertising on Facebook and Instagram.' },
      { name: 'Basics of After Effects', description: 'Motion graphics and VFX fundamentals.' },
      { name: 'Basics of Photoshop', description: 'Image editing and design fundamentals.' },
    ],
    languages: ['English', 'Hindi', 'Kannada', 'Tamil', 'Telugu'],
    seo: {
      title: 'Advanced Diploma in Film & Video Editing Bangalore: 11 Months | MatchFrame',
      description:
        '11-month Advanced Diploma in Film & Video Editing at MatchFrame Bangalore. Avid, FCPX, Premiere Pro, DaVinci Resolve, AI tools, Meta Ads, After Effects, Photoshop. 100% Placement Assistance Guarantee.',
      keywords:
        'advanced diploma film editing Bangalore, complete video editing course, film editing diploma, professional editor course Bangalore',
    },
  },
  {
    id: 'professional-diploma',
    slug: 'professional-diploma-film-video-editing',
    tier: 'diploma',
    order: 8,
    title: 'Professional Diploma in Film & Video Editing',
    tagline: 'Everything in Advanced Diploma plus a 2-month studio internship.',
    duration: '14 months',
    durationDetail: '14 months (includes 2-month internship)',
    placement: true,
    placementBadge: '100% Placement Assistance Guarantee',
    featured: true,
    description:
      'Our flagship program. Every editor and every workflow, plus a 2-month internship on real-world projects with an official experience certificate on company letterhead.',
    overview:
      'The Professional Diploma is the path students take when they want to walk into a studio interview with experience, a portfolio, and a network already in place. Includes everything in the Advanced Diploma plus a 2-month internship on live production work.',
    modules: [
      { name: 'Avid Media Composer', description: 'Professional non-linear editor for film and TV.' },
      { name: 'Final Cut Pro X', description: 'Mac-based editor with magnetic timeline workflow.' },
      { name: 'Adobe Premiere Pro', description: 'Industry-standard editor with Creative Cloud integration.' },
      { name: 'DaVinci Resolve', description: 'Editing, color grading, VFX, and audio finishing.' },
      { name: 'AI Video Creation', description: 'AI-powered video generation tools.' },
      { name: 'Meta Ads', description: 'Targeted advertising on Facebook and Instagram.' },
      { name: 'Basics of After Effects', description: 'Motion graphics and VFX fundamentals.' },
      { name: 'Basics of Photoshop', description: 'Image editing and design fundamentals.' },
      {
        name: 'Internship (2 months)',
        description:
          'Hands-on training on real-world projects with an official experience certificate issued on company letterhead upon completion.',
      },
    ],
    languages: ['English', 'Hindi', 'Kannada', 'Tamil', 'Telugu'],
    seo: {
      title: 'Professional Diploma in Film & Video Editing Bangalore: 14 Months with Internship | MatchFrame',
      description:
        '14-month Professional Diploma in Film & Video Editing at MatchFrame Bangalore. Includes 2-month studio internship with official experience certificate. 100% Placement Assistance Guarantee.',
      keywords:
        'professional diploma film editing Bangalore, video editing course with internship, film editor internship Bangalore, post production diploma',
    },
  },
];

export const getCourseBySlug = (slug) => courses.find((c) => c.slug === slug);

// Software / module brand logos for course detail pages.
// Same image sources as the homepage Hero hover effect.
export const softwareLogos = {
  'Avid Media Composer':   { label: 'Avid Media Composer',  logo: 'https://customer-assets.emergentagent.com/job_mfedit-academy/artifacts/2kqgfb0b_Avid_idOtDRQU_w_1.png' },
  'Adobe Premiere Pro':    { label: 'Adobe Premiere Pro',   logo: 'https://upload.wikimedia.org/wikipedia/commons/4/40/Adobe_Premiere_Pro_CC_icon.svg' },
  'Final Cut Pro X':       { label: 'Final Cut Pro X',      logo: 'https://upload.wikimedia.org/wikipedia/en/9/9f/2015_Final_Cut_Pro_Logo.png' },
  'DaVinci Resolve':       { label: 'DaVinci Resolve',      logo: 'https://upload.wikimedia.org/wikipedia/commons/9/90/DaVinci_Resolve_17_logo.svg' },
  'Basics of After Effects': { label: 'Adobe After Effects', logo: 'https://upload.wikimedia.org/wikipedia/commons/c/cb/Adobe_After_Effects_CC_icon.svg' },
  'Basics of Photoshop':   { label: 'Adobe Photoshop',      logo: 'https://upload.wikimedia.org/wikipedia/commons/a/af/Adobe_Photoshop_CC_icon.svg' },
  'AI Video Creation':     { label: 'Gen AI Tools',         logo: 'https://customer-assets.emergentagent.com/job_50813185-e1e2-4269-80fb-d043d7ec4164/artifacts/u7coynis_Screenshot_2026-06-07_180819-removebg-preview.png' },
  'Meta Ads':              { label: 'Meta Ads',             logo: 'https://customer-assets.emergentagent.com/job_50813185-e1e2-4269-80fb-d043d7ec4164/artifacts/yyapf2qu_idW0736plJ_logos.svg' },
};

export const additionalFeatures = [
  {
    icon: 'briefcase',
    title: 'Placement Assistance on Select Programs',
    description: 'Hiring partners come to us with vacancies. Our students get first look at openings, not the back of an application queue.',
  },
  {
    icon: 'users',
    title: 'Personal Attention & Guidance',
    description: 'Individual focus with customized learning paths tailored to your skill level and career goals.',
  },
  {
    icon: 'film',
    title: 'Learn with Professional Footage',
    description: 'Work with real professional footage used in the industry for authentic learning experience.',
  },
  {
    icon: 'clock',
    title: 'Unlimited Lab Hours',
    description: 'Practice as much as you want with unlimited access to our editing labs and workstations.',
  },
  {
    icon: 'award',
    title: 'Professional Trainers',
    description: 'Highly experienced trainers from the media industry teaching editing theory, special effects, and transitions.',
  },
  {
    icon: 'folder',
    title: 'Project-Based Learning',
    description: 'Create your own professional portfolio with real projects and get certified upon completion.',
  },
];

export const testimonials = [
  {
    id: 1,
    name: 'Naveena Priya',
    role: 'Adobe Premiere Pro Student',
    image: null,
    text: "I'm a student from MatchFrame Editing School. It's been 3 months and I'm learning Adobe Premiere Pro. I'm really glad that I chose this institute. The trainers are friendly and teach everything, covering all styles of editing. I've learnt so many things from P.R. Soundararaj sir, who has won many awards for his excellence in the Film Editing industry. This institute has the best guidance and good teaching technology.",
    rating: 5,
  },
  {
    id: 2,
    name: 'Adiya P',
    role: 'MatchFrame Student, Koramangala',
    image: null,
    text: "I am a student of MatchFrame Editing School, Koramangala, and my experience has been excellent. The institute provides a professional learning environment, and we get real raw footage to edit, which helps us build strong practical skills. All systems here are Mac, giving us an industry-level workflow experience. Learning under veteran Kannada film editor P. R. Soundar Raj sir, who has edited over 125 films, is truly inspiring. I genuinely recommend MatchFrame Editing School to anyone who wants to learn professional video editing with real guidance and hands-on practice.",
    rating: 5,
  },
  {
    id: 3,
    name: 'Raki Gowda',
    role: 'Avid, DaVinci Resolve & Premiere Pro',
    image: null,
    text: 'I had an excellent experience at MatchFrame Editing School. The faculty was knowledgeable and supportive, explaining advanced editing techniques in simple, understandable steps. We got to work on real film footage and assignments, which gave me hands-on confidence. The infrastructure was top notch with high-end computers and the latest editing software. The batch sizes were small, so I received one-on-one feedback and personalized guidance. They also helped with internship placements and industry exposure.',
    rating: 5,
  },
  {
    id: 4,
    name: 'Zumba GetFit',
    role: 'MatchFrame Alumni',
    image: null,
    text: 'In the entire South India, as far as my knowledge goes, the best place to learn editing at the best cost is MatchFrame Institute in Bengaluru Koramangala. Teaching method with practical experience was awesome. Thank you Madan sir and Soundarraj sir and all the assistants for teaching and giving us the real editing experience.',
    rating: 5,
  },
  {
    id: 5,
    name: 'Rahul Parameshwar',
    role: 'Film Editing Course Student',
    image: null,
    text: 'Very useful film editing course with strong practical focus. Trainers share industry-relevant techniques. Staff is friendly and supportive throughout the training.',
    rating: 5,
  },
  {
    id: 6,
    name: 'Shaun',
    role: 'MatchFrame Alumni',
    image: null,
    text: 'They follow a one-to-one approach for teaching, which is helpful for all the students. Mr. Soundaraj sir\'s guidance on every project or edit we make helps us step up our editing.',
    rating: 5,
  },
  {
    id: 7,
    name: 'Bala Muralikrishna',
    role: 'Premiere Pro & DaVinci Resolve',
    image: null,
    text: 'Best place to learn advanced-level editing and colour grading. Hands-on with real footage feels like starting with experience. The faculty and Soundar sir are very helpful in terms of sharing experience.',
    rating: 5,
  },
  {
    id: 8,
    name: 'Dinesh Karthika',
    role: 'Professional Diploma in Film & Video Editing',
    image: null,
    text: 'Best place to learn professional film editing with industry-standard guidance. Friendly mentors and practical training made learning easy and effective.',
    rating: 5,
  },
  {
    id: 9,
    name: 'Adarsha Adi',
    role: 'MatchFrame Alumni',
    image: null,
    text: 'Best editing institute for all types of learners who love to learn editing education. Great mentors and flexible fee structure. Thanks to P.R. Soundararaj sir for the support and guidance.',
    rating: 5,
  },
];

export const stats = [
  { number: '750+', label: 'Students Trained' },
  { number: '500+', label: 'Students Placed' },
  { number: '125+', label: 'Hiring Partners' },
  { number: '25+', label: 'Years Experience' },
];

// Student YouTube Shorts reviews. Embedded video-only.
export const studentShorts = [
  { id: 'f_ddCB3KL6A', url: 'https://www.youtube.com/shorts/f_ddCB3KL6A' },
  { id: 'r3V0B9a4ZrQ', url: 'https://www.youtube.com/shorts/r3V0B9a4ZrQ' },
  { id: '7OBfh85AsLg', url: 'https://www.youtube.com/shorts/7OBfh85AsLg' },
  { id: 'Z0Z6T7kAE6U', url: 'https://www.youtube.com/shorts/Z0Z6T7kAE6U' },
  { id: '8ZzQOKNSGwQ', url: 'https://www.youtube.com/shorts/8ZzQOKNSGwQ' },
];

export const faqs = [
  {
    question: 'Do you offer offline classes?',
    answer:
      'Yes, all our classes are conducted offline with professional experienced trainers at our Koramangala campus in Bangalore. Classes run from 8 AM to 8 PM Monday to Saturday with flexible batch timings for working professionals and students.',
  },
  {
    question: 'Do you offer online classes?',
    answer:
      'We do offer online classes to a few students, but it is a case-by-case decision based on factors like your workstation setup, internet connectivity, software availability, and learning goals. Please contact our admissions office to find out whether you qualify for online classes.',
  },
  {
    question: 'What types of courses do you offer?',
    answer:
      'We offer 8 programs across two tiers. Short Courses include Single Software for Editing, Master in AI Video Creation, Master in Digital Color Grading, and AI Integrated Video Editing. Diploma Programs include Master in Film Editing, Master in Social Media Editing, Advanced Diploma, and Professional Diploma. Software covered: Avid Media Composer, Final Cut Pro X, Adobe Premiere Pro, DaVinci Resolve, After Effects, Photoshop, AI video tools, and Meta Ads.',
  },
  {
    question: 'Do you provide internship opportunities?',
    answer:
      'Yes. The Professional Diploma in Film & Video Editing includes a 2-month internship on real-world projects, with an official experience certificate issued on company letterhead upon completion.',
  },
  {
    question: 'How long are the courses?',
    answer:
      'Short courses range from 1 month (Master in AI Video Creation) up to 4 months (AI Integrated Video Editing). Diploma programs range from 4 months (Master in Film Editing) up to 14 months (Professional Diploma, including the 2-month internship).',
  },
  {
    question: 'What is the course fee?',
    answer:
      'Course fees start from INR 25,000 and vary depending on the course you choose. Please contact us at +91 96324 06669 for fee-related questions and to discuss the right program for your goals.',
  },
  {
    question: 'Do you offer payment plans?',
    answer:
      'Yes, we offer custom payment and installment plans to make learning accessible. Speak to our admissions offices for personalized options.',
  },
  {
    question: 'Is Video editing easy to learn?',
    answer:
      'Editing is a relatively simple process to understand. Once you have learned the basics you\'ll be able to use your new skills to significantly raise the quality of your videos.',
  },
  {
    question: 'Do I need prior experience or computer skills to join?',
    answer:
      'No. Our students are from age 12 to 60+ and come from many different backgrounds. Some of them have never used computers regularly before. We teach everyone from the basics, at a pace that works for you. No prior experience is needed.',
  },
  {
    question: 'Will I get support in building my portfolio?',
    answer:
      'Yes. We provide full portfolio development support to help you showcase your best work. You\'ll graduate with a professional portfolio that highlights your editing, color grading, and storytelling skills. A key asset for job applications and freelance work.',
  },
  {
    question: 'Why shouldn\'t I just learn editing from YouTube?',
    answer:
      'While YouTube offers free tutorials, our professional training provides structured curriculum with carefully curated practice footage, personal accountability, 1-on-1 training with expert mentors, dedicated doubt-clearing sessions, access to internships and real job opportunities, guidance in choosing the right software for your career goals, and insider industry insights you won\'t find online. We ensure you build job-ready skills systematically, not just scattered knowledge.',
  },
  {
    question: 'Is the course more theory or hands-on?',
    answer:
      'Our training is highly focused on practical skills. While you\'ll learn essential theory, we believe the best way to master editing is by doing, not just watching. You\'ll spend most of your time working directly on real editing tasks.',
  },
  {
    question: 'Will I get to work on real-world projects during the course?',
    answer:
      'Yes. You\'ll work on real-world projects like music videos, short films, and other assignments. Our training centre, located in a corporate building, simulates a professional studio, helping you transition into your future workplace much more smoothly.',
  },
  {
    question: 'Who are the founders of MatchFrame Editing School?',
    answer:
      'MatchFrame Editing School in Bangalore was founded by PR Soundarraj and Madan G, both highly experienced film editors.\n\nPR Soundarraj is a professional film editor with over 25 years of experience and has worked on more than 125 films. He has received several prestigious awards, including the Karnataka State Award for Best Film Editor (2012) for the movie Sagar, and the Indywood Excellence Award (2017) from the Telangana Government for Best Post Production.\n\nMadan G began his career as a promo editor at Seven Crest Communication and went on to edit trailers and songs for several films. His notable works include trailer edits for Payanam, Singam Puli, Dhoni, Sivalinga, Maragatha Naanayam, and promo edits for Kaala, Vada Chennai, and more across Tamil and Kannada cinema.\n\nTogether, they bring real-world industry experience and a passion for mentoring the next generation of editors.',
  },
];
